import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import Loading from '../../../../components/Loading';
import PaginationBar from '../../../../components/PaginationBar/PaginationBar';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { listCandidates } from '../../../../services/hr/recruitment/candidatesApi';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import { Container } from '../../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';
import {
   ActionBar,
   ActionBarLeft,
   ActionBarRight,
} from '../../../../styles/layout/ActionBar';
import { FormGrid } from '../../../../styles/layout/FormGrid';
import { Field } from '../../../../styles/forms/Field';
import { TableWrapper } from '../../../../styles/table/TableWrapper';
import { Table } from '../../../../styles/table/Table';
import { ClickableRow, TextLink } from '../../../../styles/ui/Link';
import { CtaButton, SmallButton } from '../../../../styles/ui/Button';

import JobPositionSelect from '../components/JobPositionSelect';
import OriginSelect from '../components/OriginSelect';
import StatusBadge from '../components/StatusBadge';
import PullToOpeningModal from '../components/PullToOpeningModal';
import { formatDateBr, isOldRegistration } from '../utils/format';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Banco de currículos (handoff 0061 §5.1).
//
// Lista os candidatos SEM vaga (only_pool=true) — o funil dos que estão em
// processo é visto de dentro da vaga (fatia 2). Segue navegacao.md cap.1
// (URL-driven, busca explícita, sentinela applied=1, navigate(-1) no Voltar do
// detalhe). SEM ordenação por cabeçalho: o backend ordena fixo por created_at
// DESC e não expõe order_by (1.10.4). Ação de linha: puxar pra vaga.
// ---------------------------------------------------------------------------

const ITEMS_PER_PAGE = 20;

// Chaves snake_case = nomes dos query params da URL = nomes do backend. A
// tradução para os args camelCase do service acontece em toServiceArgs().
const DEFAULT_FILTERS = {
   search: '',
   desired_position_id: '',
   origin_id: '',
};

function readFiltersFromUrl(searchParams) {
   const next = { ...DEFAULT_FILTERS };
   Object.keys(DEFAULT_FILTERS).forEach((key) => {
      const value = searchParams.get(key);
      if (value !== null && value !== '') next[key] = value;
   });
   return next;
}

// snake_case (URL/estado) → camelCase (args do service). only_pool é fixo.
function toServiceArgs(filters, page) {
   return {
      page,
      perPage: ITEMS_PER_PAGE,
      onlyPool: true,
      search: filters.search,
      desiredPositionId: filters.desired_position_id,
      originId: filters.origin_id,
   };
}

export default function CandidatePoolList() {
   const navigate = useNavigate();
   const { hasPermission } = usePermissions();
   const canCreate = hasPermission('hr:candidate:create');
   const canPull = hasPermission('hr:candidate:update');

   const [searchParams, setSearchParams] = useSearchParams();
   const hasInitialQueryParams = searchParams.toString() !== '';

   // Rascunho (ligado aos inputs) × aplicado (último efetivamente pesquisado).
   const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
   const [appliedFilters, setAppliedFilters] = useState(filters);
   const [currentPage, setCurrentPage] = useState(() => {
      const fromUrl = Number(searchParams.get('page'));
      return Number.isFinite(fromUrl) && fromUrl > 0 ? fromUrl : 1;
   });

   const [candidates, setCandidates] = useState([]);
   const [totalPages, setTotalPages] = useState(1);
   const [totalCount, setTotalCount] = useState(0);
   const [isLoading, setIsLoading] = useState(false);

   // Alvo do "puxar pra vaga" (candidato da linha) — controla o modal.
   const [pullTarget, setPullTarget] = useState(null);

   const didInitialFetch = useRef(false);

   // applied=1 marca "houve busca" (busca sem filtros não gera param nenhum).
   const commitToUrl = useCallback(
      (values, page) => {
         const params = { applied: '1' };
         Object.keys(DEFAULT_FILTERS).forEach((key) => {
            if (values[key]) params[key] = values[key];
         });
         if (page > 1) params.page = String(page);
         setSearchParams(params, { replace: true });
      },
      [setSearchParams],
   );

   const fetchCandidates = useCallback(async (values, page) => {
      setIsLoading(true);
      try {
         const result = await listCandidates(toServiceArgs(values, page));
         setCandidates(result.candidates);
         setTotalPages(result.totalPages);
         setTotalCount(result.total);
         setCurrentPage(result.page);
      } catch (err) {
         const apiMessage =
            err?.response?.data?.errors?.[0]?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao buscar candidatos.';
         toast.error(apiMessage);
      } finally {
         setIsLoading(false);
      }
   }, []);

   // Busca inicial só quando a URL traz filtros (deep-link / F5 / voltar).
   useEffect(() => {
      if (didInitialFetch.current) return;
      didInitialFetch.current = true;
      if (!hasInitialQueryParams) return;
      fetchCandidates(appliedFilters, currentPage);
   }, [fetchCandidates, hasInitialQueryParams, appliedFilters, currentPage]);

   const updateField = (key, value) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
   };

   const handleSearch = (event) => {
      event.preventDefault();
      setAppliedFilters(filters);
      commitToUrl(filters, 1);
      fetchCandidates(filters, 1);
   };

   const handleClear = () => {
      setFilters(DEFAULT_FILTERS);
      setAppliedFilters(DEFAULT_FILTERS);
      setCandidates([]);
      setTotalCount(0);
      setTotalPages(1);
      setCurrentPage(1);
      setSearchParams({}, { replace: true });
   };

   const handlePageChange = (page) => {
      commitToUrl(appliedFilters, page);
      fetchCandidates(appliedFilters, page);
   };

   // Após puxar pra vaga, o candidato deixa o banco (only_pool) → refaz a busca.
   const handlePulled = () => {
      fetchCandidates(appliedFilters, currentPage);
   };

   const hasResults = candidates.length > 0;

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Banco de currículos</PageTitle>
               <PageSubtitle>
                  Candidatos aguardando entrada em um processo seletivo
               </PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               {canCreate && (
                  <CtaButton
                     type="button"
                     onClick={() => navigate('/hr/recruitment/candidates/new')}
                  >
                     + Cadastrar candidato
                  </CtaButton>
               )}
            </PageHeaderActions>
         </PageHeader>

         {/* ─── Filtros ───────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>
            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field className="col-4">
                        <label htmlFor="filter-search">Nome do candidato</label>
                        <input
                           id="filter-search"
                           type="text"
                           placeholder="Parte do nome..."
                           value={filters.search}
                           onChange={(e) =>
                              updateField('search', e.target.value)
                           }
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-position">Cargo pretendido</label>
                        <JobPositionSelect
                           id="filter-position"
                           value={filters.desired_position_id}
                           onChange={(value) =>
                              updateField('desired_position_id', value)
                           }
                           placeholder="Todos"
                        />
                     </Field>

                     <Field className="col-4">
                        <label htmlFor="filter-origin">Origem</label>
                        <OriginSelect
                           id="filter-origin"
                           value={filters.origin_id}
                           onChange={(value) => updateField('origin_id', value)}
                           placeholder="Todas"
                        />
                     </Field>
                  </FormGrid>

                  <ActionBar style={{ marginTop: 16 }}>
                     <ActionBarLeft />
                     <ActionBarRight>
                        <TextLink
                           as="button"
                           type="button"
                           onClick={handleClear}
                           disabled={isLoading}
                        >
                           Limpar
                        </TextLink>
                        <CtaButton type="submit" disabled={isLoading}>
                           Pesquisar
                        </CtaButton>
                     </ActionBarRight>
                  </ActionBar>
               </form>
            </SectionBody>
         </Section>

         {/* ─── Resultados ────────────────────────────────────────────────── */}
         <Section>
            <SectionHeader>
               <SectionTitle>
                  Resultados{totalCount ? ` (${totalCount})` : ''}
               </SectionTitle>
            </SectionHeader>
            <SectionBody>
               {hasResults ? (
                  <>
                     <TableWrapper>
                        <Table>
                           <thead>
                              <tr>
                                 <th>Candidato</th>
                                 <th>Cargo pretendido</th>
                                 <th>Origem</th>
                                 <th>Cadastrado em</th>
                                 <th className="center">Ações</th>
                              </tr>
                           </thead>
                           <tbody>
                              {candidates.map((candidate) => (
                                 <ClickableRow
                                    key={candidate.id}
                                    onClick={() =>
                                       navigate(
                                          `/hr/recruitment/candidates/${candidate.id}`,
                                       )
                                    }
                                 >
                                    <td>
                                       {candidate.full_name}
                                       {isOldRegistration(
                                          candidate.created_at,
                                       ) && (
                                          <span style={{ marginLeft: 8 }}>
                                             <StatusBadge variant="amber">
                                                Cadastro antigo
                                             </StatusBadge>
                                          </span>
                                       )}
                                    </td>
                                    <td>
                                       {candidate.desired_position
                                          ?.position_name ?? '—'}
                                    </td>
                                    <td>{candidate.origin?.name ?? '—'}</td>
                                    <td>{formatDateBr(candidate.created_at)}</td>
                                    <td className="center">
                                       {canPull && (
                                          <SmallButton
                                             type="button"
                                             $intent="cta"
                                             onClick={(e) => {
                                                e.stopPropagation();
                                                setPullTarget(candidate);
                                             }}
                                          >
                                             Puxar pra vaga
                                          </SmallButton>
                                       )}
                                    </td>
                                 </ClickableRow>
                              ))}
                           </tbody>
                        </Table>
                     </TableWrapper>

                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        disabled={isLoading}
                     />
                  </>
               ) : (
                  <EmptyState
                     title="Nenhum candidato no banco"
                     description="Ajuste os filtros e clique em Pesquisar, ou cadastre um novo candidato."
                  />
               )}
            </SectionBody>
         </Section>

         <PullToOpeningModal
            isOpen={Boolean(pullTarget)}
            onClose={() => setPullTarget(null)}
            candidate={pullTarget}
            onSaved={handlePulled}
         />
      </Container>
   );
}
